<template>
	<header
		:class="`${isAbnahme ? 'header--abnahme' : ''} ${!isLoggedIn ? 'header--public' : ''} ${menuVisible ? 'is--open' : ''} ${isClosing ? 'menu--closing' : ''}`"
	>
		<div class="header--inner">
			<RouterLink :to="isLoggedIn ? '/' : '/login'" id="logo">
				<img src="../assets/logo_werberat.svg" alt="Werberat Logo" />
			</RouterLink>

			<template v-if="isLoggedIn && user">
				<el-menu :default-active="'/'" mode="horizontal" close-on-click-outside id="nav" :ellipsis="false" v-if="!isBerichtPage">
					<el-menu-item v-for="li in menu" :key="li.path" :index="li.path">
						<RouterLink :to="li.path">{{ li.displayName }}</RouterLink>
					</el-menu-item>
				</el-menu>

				<div class="header--right">
					<el-icon @click="toggleSearchDrawerOpen" v-if="!isWerberatUser"><Search /></el-icon>
					<TheButton @buttonclick="logout()" :text="true" class="button--logout">Logout</TheButton>
					<el-avatar :title="user.email + ' (' + userRoles + ')'" :size="40" id="usericon"> {{ user.initials }} </el-avatar>

					<!-- menu toggle button -->
					<div v-if="isMobile" id="menuicon" @click="toggleMenu" aria-label="Menü">
						<transition name="el-zoom-in-center">
							<MenuIcon v-if="!menuVisible" />
						</transition>
						<transition name="el-zoom-in-center">
							<XIcon v-if="menuVisible" />
						</transition>
					</div>
				</div>
			</template>

			<template v-else>
				<div class="header--right">
					<a href="https://www.werberat.at/" title="Werberat-Website aufrufen" class="link" target="_blank">Zurück zu werberat.at</a>
				</div>
			</template>
		</div>
	</header>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';
import TheButton from '../components/TheButton.vue';
import MenuIcon from '../assets/icons/MenuIcon.vue';
import XIcon from '../assets/icons/XIcon.vue';

import { Search, Menu } from '@element-plus/icons-vue';
import { useStore, authUser } from '../store';
import { logout } from '../requests/auth';
import { ElMessage } from 'element-plus';
import { texts } from '../utils/texts';
import { checkIsMobile } from '@/utils/functions';

interface MenuItem {
	name: string;
	displayName: string;
}

type Menu = any[];

export default defineComponent({
	name: 'TheHeader',
	data() {
		return {
			menu: [] as Menu,
			menuVisible: false,
			isClosing: false,
		};
	},
	setup() {
		const store = useStore();
		const loggedin = computed(() => store.state.loggedin);
		const isMobile = computed(() => store.state.isMobile);

		const roles = computed(() => (authUser.value ? authUser.value.roles?.map((r) => r.name).join(', ') : ''));

		return {
			store,
			loggedin,
			isMobile,
			user: authUser,
			userRoles: roles,
		};
	},
	components: {
		Search,
		Menu,
		TheButton,
		MenuIcon,
		XIcon,
	},
	computed: {
		isLoggedIn() {
			return this.loggedin && this.user !== null;
		},
		isAbnahme() {
			return this.store.state.environment === 'abnahme';
		},
		isWerberatUser() {
			if (!this.user) return false;
			const userRoles = this.user.roles ? this.user.roles.map((role) => role.name) : [];
			return (
				userRoles.includes('Werberat') ||
				userRoles.includes('Junger Werberat') ||
				userRoles.includes('Kleiner Senat A') ||
				userRoles.includes('Kleiner Senat B')
			);
		},
		isBerichtPage() {
			return this.$route.name == 'Bericht';
		},
	},
	methods: {
		async logout() {
			const logoutResult = await logout();
			if (logoutResult.success) {
				this.store.dispatch('logout');
				this.$router.push({ name: 'Login' });
			} else {
				console.error('Logout failed');
				ElMessage({ message: texts.formErrors.logoutFailed, type: 'error', duration: 3000 });
			}
		},
		toggleSearchDrawerOpen() {
			this.store.dispatch('toggleSearchDrawerOpen');
		},
		toggleMenu() {
			this.menuVisible = !this.menuVisible;
			if (this.menuVisible === true && this.isMobile) {
				document.body.classList.add('is-fixed');
			} else {
				document.body.classList.remove('is-fixed');
			}
		},
		getMenuItems(): MenuItem[] {
			if (!this.loggedin || !this.user) return [];

			const userRoles = this.user.roles ? this.user.roles.map((role) => role.name) : [];
			let menuItems: MenuItem[] = [];

			if (userRoles.includes('Admin') || userRoles.includes('Super-Admin')) {
				menuItems = [
					{ name: 'Dashboard', displayName: 'Dashboard' },
					{ name: 'Beschwerden', displayName: 'Beschwerden' },
					{ name: 'Fälle', displayName: 'Fälle' },
					{ name: 'Werberatsentscheidungen', displayName: 'Entscheidungen' },
					{ name: 'Statistik', displayName: 'Statistik' },
					{ name: 'User', displayName: 'Benutzer' },
					{ name: 'Kontakte', displayName: 'Kontakte' },
				];
			} else if (userRoles.includes('Vorstand')) {
				menuItems = [
					{ name: 'Dashboard', displayName: 'Dashboard' },
					{ name: 'FälleAktiv', displayName: 'Aktive Fälle' },
					{ name: 'Fälle', displayName: 'Entscheidungen' },
					{ name: 'Statistik', displayName: 'Statistik' },
				];
			} else if (this.isWerberatUser) {
				menuItems = [
					{ name: 'Dashboard', displayName: 'Dashboard' },
					{ name: 'Fälle', displayName: 'Entscheidungen' },
				];
			}

			return menuItems;
		},
		setMenu() {
			const menuItems = this.getMenuItems();
			const routes = this.$router.getRoutes();

			this.menu = menuItems.map((m) => {
				let route = routes.find((r) => r.name === m.name);
				return {
					...route,
					displayName: m.displayName,
				};
			});
		},
	},
	watch: {
		loggedin() {
			this.setMenu();
		},
		'$route.fullPath'() {
			if (this.menuVisible) {
				this.toggleMenu();
			}
		},
		isMobile(newIsMobile) {
			if (newIsMobile === true) {
				this.menuVisible = false;
			}
		},
	},
	mounted() {
		this.setMenu();

		if (!this.isMobile) {
			this.menuVisible = true;
		}
	},
});
</script>

<style lang="scss">
@use '../scss/vars.scss' as *;

$logo_height: 45px;
$header_padding-x: 2rem;

.header--abnahme {
	background-color: var(--el-color-abnahme);
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.5s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active in <2.1.8 */ {
	opacity: 0;
}

header {
	display: inline-block;
	width: 100%;
	padding: 1.5rem $header_padding-x;
	background-color: #fff;
	box-sizing: border-box;

	.header--inner {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		max-width: $header_maxwidth;
		margin: 0 auto;
	}

	&.header--public {
		.header--right {
			.link {
				text-decoration: none;
				margin: 0;
			}
		}
	}
}

// BURGER ICON //
#menuicon {
	position: relative;
	background-color: transparent;
	border: 0 !important;
	width: 25px;
	height: 25px;
	padding: 0;
	position: relative;
	z-index: 100;
	cursor: pointer;

	svg {
		position: absolute;
		top: 50%;
		left: 50%;
		height: 100%;
		width: 100%;
		transform: translate(-50%, -50%);
	}
}

// BURGER ICON END //

// MENU NAV //
#nav.el-menu {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	border: 0;
	width: 100%;

	.el-menu-item {
		margin: 0 0.5rem;
		border-radius: 8px;
		border: 0px;
		height: max-content;

		a {
			border-radius: 8px;
		}

		&:focus,
		&:hover,
		.router-link-active {
			background-color: var(--el-color-lightgrey-1);
		}

		a {
			padding: 0.75rem;
			line-height: 1;
			font-size: 1rem;
		}
	}
}

// MENU NAV END //

#logo {
	height: $logo_height;
	margin-right: 2rem;
	img {
		height: 100%;
		width: auto;
	}
}

.header--right {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	width: 140px;

	.el-icon {
		cursor: pointer;
	}

	> * {
		margin-left: 0.5rem;
		white-space: pre;
	}

	.el-button.is-round.is-text {
		text-decoration: none;

		&:hover {
			background-color: transparent;
		}
	}
}

#usericon {
	background-color: var(--el-color-lightgrey-3);
	color: #fff;
	font-size: 0.8rem;
}

/* mobile styles */
@media screen and (max-width: $mobile_maxwidth) {
	$headerright_bottom: 1rem;
	$header_padding-x: 1rem;

	header {
		padding: 1.5rem $header_padding-x;
	}

	header:not(.is--open) {
		#nav.el-menu,
		.button--logout,
		#usericon {
			display: none;
		}
	}

	header.header--public {
		.header--inner {
			flex-direction: column;
			align-items: flex-start;

			.header--right {
				display: none;
			}
		}
	}

	header {
		/* search icon */
		.el-icon {
			font-size: 1.7rem;
			margin-right: 0.75rem;
		}

		&.is--open {
			position: fixed;
			top: 0;
			left: 0;
			bottom: 0;
			right: 0;
			z-index: 1000001;

			overflow: hidden;

			#nav.el-menu {
				flex-direction: column;
				flex-wrap: nowrap;
				position: absolute;
				top: calc($logo_height + $header_padding-x + 1rem);
				left: 0;
				right: 0;
				bottom: 0;
				padding-bottom: calc(var(--el-avatar-size) + $headerright_bottom + 5rem);
				height: calc(100vh - $logo_height + 1rem);
				background-color: var(--el-color-lightgrey-1);
				overflow: scroll;

				.el-menu-item {
					width: 100%;
					justify-content: flex-start;
					margin: 0;

					border-bottom: 1px solid var(--el-color-lightgrey-2);
					border-radius: 0;

					&:first-child {
						margin-top: 2rem;
					}

					a {
						padding: 1.5rem 2rem;
						font-size: 1.25rem;
					}
				}
			}

			.header--right {
				/* background for usericon + logout button at the bottom */
				&:after {
					content: '';
					width: 100%;
					background-color: var(--el-color-lightgrey-1);
					display: inline-block;
					height: calc(var(--el-avatar-size) + $headerright_bottom + 1rem);
					position: fixed;
					bottom: 0;
					left: 0;
					z-index: 5;
				}

				.button--logout,
				#usericon {
					position: absolute;
					bottom: $headerright_bottom;

					z-index: 99999;
				}

				.button--logout {
					right: 2rem;
				}

				#usericon {
					left: 2rem;
				}
			}
		}
	}
}
</style>
